import React from 'react';
import { Helmet } from 'react-helmet'; 
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import HeroSection from './components/HeroSection';
import FeaturedProjects from './components/FeaturedProjects';
import AboutPreview from './components/AboutPreview';
import ClientLogos from './components/ClientLogos';
import TestimonialCarousel from './components/TestimonialCarousel';
import CallToAction from './components/CallToAction';

const Homepage = () => {
  return (
    <div id="main-content" className="min-h-screen bg-transparent">
      <Helmet>
        {/* ✅ Basic Metadata */}
        <title>Sudhan R | Space Tech & Software Engineer</title>
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta
          name="description"
          content="Portfolio of Sudhan R: Software Engineer and Space Tech Researcher specializing in AI, remote sensing, and full-stack development."
        />
        <meta
          name="keywords"
          content="Sudhan R, Rajan Sudhan, Space Tech, ISRO, NASA STEM, NARL, NESAC UAV, GIS, Python, Machine Learning, Full-Stack Developer, React.js, Remote Sensing, Software Engineer"
        />
        <meta name="author" content="Sudhan R" />

        {/* ✅ Canonical */}
        <link rel="canonical" href="https://sudhan.live/" />

        {/* ✅ Open Graph (Facebook, LinkedIn) */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url" content="https://sudhan.live/" />
        <meta property="og:site_name" content="Sudhan R Portfolio" />
        <meta property="og:title" content="Sudhan R | Space-Tech Innovator, Developer & Researcher" />
        <meta
         property="og:description"
          content="Explore the impact-driven portfolio of Sudhan R: bridging the gap between space research, geospatial analytics, and full-stack software engineering."
        />
        <meta property="og:image" content="https://sudhan.live/assets/images/Sudhan_2026_v3.png" />

        {/* ✅ Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://sudhan.live/" />
        <meta name="twitter:title" content="Sudhan R | Remote Sensing, Space-Tech & Development" />
        <meta
          name="twitter:description"
          content="Explore impactful projects bridging space research, UAV analytics, and modern full-stack web applications."
        />
        <meta name="twitter:image" content="https://sudhan.live/assets/images/Sudhan_2026_v3.png" />

        {/* ✅ Structured Data: JSON-LD for Google Rich Snippets */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Person",
                "@id": "https://sudhan.live/#person",
                "name": "Sudhan R",
                "givenName": "Sudhan",
                "familyName": "R",
                "alternateName": "Sudhan-23082004",
                "url": "https://sudhan.live/",
                "image": {
                  "@type": "ImageObject",
                  "@id": "https://sudhan.live/#avatar",
                  "url": "https://sudhan.live/assets/images/Sudhan_2026_v3.png",
                  "caption": "Sudhan R"
                },
                "description": "Sudhan R is a Computer Science Engineer, Software Developer, and Space Technology Researcher specializing in Artificial Intelligence, Remote Sensing, Earth Observation, Geospatial Analytics, Machine Learning, Full-Stack Software Development, Scientific Computing, Satellite Data Processing, UAV Data Processing, and Data Visualization.",
                "sameAs": [
                  "https://www.linkedin.com/in/sudhan23082004/",
                  "https://github.com/Sudhan-23082004",
                  "https://orcid.org/0009-0007-0461-2150"
                ],
                "knowsLanguage": [
                  {
                    "@type": "Language",
                    "name": "English",
                    "alternateName": "en"
                  },
                  {
                    "@type": "Language",
                    "name": "Tamil",
                    "alternateName": "ta"
                  },
                  {
                    "@type": "Language",
                    "name": "Malayalam",
                    "alternateName": "ml"
                  }
                ],
                "knowsAbout": [
                  "Artificial Intelligence",
                  "Machine Learning",
                  "Deep Learning",
                  "Python",
                  "Java",
                  "C++",
                  "JavaScript",
                  "TypeScript",
                  "React",
                  "Node.js",
                  "Cloudflare",
                  "GIS",
                  "Remote Sensing",
                  "Earth Observation",
                  "Satellite Data Processing",
                  "UAV Data Processing",
                  "Drone Mapping",
                  "Geospatial Analytics",
                  "Computer Vision",
                  "Image Processing",
                  "Scientific Computing",
                  "Data Science",
                  "Data Visualization",
                  "Cloud Computing",
                  "Software Engineering",
                  "Full Stack Development",
                  "Open Source Software",
                  "Research"
                ],
                "hasOccupation": [
                  {
                    "@type": "Occupation",
                    "name": "Computer Science Engineer",
                    "description": "Applies computer science theory and software engineering principles to design, develop, and test software systems and applications."
                  },
                  {
                    "@type": "Occupation",
                    "name": "Software Developer",
                    "description": "Develops full-stack applications, open-source software, and interactive web tools."
                  },
                  {
                    "@type": "Occupation",
                    "name": "Space Technology Researcher",
                    "description": "Conducts research in satellite data processing, Earth observation, drone mapping, remote sensing, and geospatial intelligence."
                  }
                ]
              },
              {
                "@type": "WebSite",
                "@id": "https://sudhan.live/#website",
                "url": "https://sudhan.live/",
                "name": "Sudhan R Portfolio",
                "description": "Official personal portfolio website of Sudhan R - Computer Science Engineer, Software Developer, and Space Technology Researcher.",
                "publisher": {
                  "@id": "https://sudhan.live/#person"
                },
                "about": {
                  "@id": "https://sudhan.live/#person"
                },
                "inLanguage": "en",
                "copyrightYear": "2026"
              },
              {
                "@type": "ProfilePage",
                "@id": "https://sudhan.live/#profilepage",
                "url": "https://sudhan.live/",
                "name": "Sudhan R - Profile & Portfolio",
                "description": "Official profile page and professional landing of Sudhan R. Showcasing software systems, research in space technology, Earth observation, and AI solutions.",
                "isPartOf": {
                  "@id": "https://sudhan.live/#website"
                },
                "mainEntity": {
                  "@id": "https://sudhan.live/#person"
                },
                "breadcrumb": {
                  "@id": "https://sudhan.live/#breadcrumb"
                }
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://sudhan.live/#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://sudhan.live/"
                  }
                ]
              }
            ]
          }
          `}
        </script>
      </Helmet>
      <Header />
      
      <main>
        <HeroSection />
        <FeaturedProjects />
        <AboutPreview />
        <ClientLogos />
        <TestimonialCarousel />
        <CallToAction />
      </main>

      <Footer />
    </div>
  );
};


export default Homepage;